'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { Collapsible } from '../../../../components/collapsible';
import { Text } from '../../../../components/styled/text';
import { type TracePhase, type TraceToolCall } from '../types';
import { toTraceReadFileEntry } from '../utils/read-file-tool';
import { getTraceTextShimmerStyle } from '../utils/trace-text-shimmer';
import { TraceExpandChevron } from './TraceExpandChevron';
import { TraceReadFilesPanel } from './TraceReadFilesPanel';
import { TraceReadToolCard } from './TraceReadToolCard';

type TraceReadToolCardGroupProps = {
  toolCalls: TraceToolCall[];
  tracePhase?: TracePhase;
  onHideMessage?: () => void;
};

export function TraceReadToolCardGroup({
  toolCalls,
  tracePhase = 'attack',
  onHideMessage,
}: TraceReadToolCardGroupProps) {
  const t = useTranslations('designSystem.chat');
  const isCoPilotTrace = tracePhase === 'co-pilot';
  const [isOpen, setIsOpen] = useState(isCoPilotTrace);

  const entries = useMemo(
    () =>
      toolCalls.map((toolCall) =>
        toTraceReadFileEntry(toolCall, { tracePhase }),
      ),
    [toolCalls, tracePhase],
  );

  if (toolCalls.length === 0) {
    return null;
  }

  if (isCoPilotTrace && toolCalls.length === 1 && toolCalls[0]) {
    return (
      <TraceReadToolCard
        toolCall={toolCalls[0]}
        tracePhase={tracePhase}
        onHideMessage={onHideMessage}
      />
    );
  }

  const hasAnyResult = entries.some(
    (entry) => entry.output.length > 0 || Boolean(entry.error?.trim()),
  );

  const headerLabel = t('readTool.readFilesCount', { count: toolCalls.length });
  const allSettled = toolCalls.every(
    (toolCall) =>
      toolCall.status === 'completed' || toolCall.status === 'error',
  );
  const showShimmer = !hasAnyResult && !allSettled;
  const shimmerStyle = getTraceTextShimmerStyle(showShimmer);

  if (!hasAnyResult) {
    return (
      <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
        <Text
          fontSize="xs"
          color="fg.muted"
          animationStyle={showShimmer ? 'shimmer' : undefined}
          style={shimmerStyle}
        >
          {headerLabel}
        </Text>
      </HStack>
    );
  }

  if (isCoPilotTrace) {
    return (
      <Box
        bg="bg.subtle"
        borderWidth="1px"
        borderColor="border.neutral.muted"
        borderRadius="md"
        px={3}
        py={2}
        width="100%"
      >
        <TraceReadFilesPanel entries={entries} tracePhase={tracePhase} />
      </Box>
    );
  }

  return (
    <VStack alignItems="stretch" gap={1} width="100%">
      <Collapsible.Root
        open={isOpen}
        onOpenChange={({ open }) => setIsOpen(open)}
        lazyMount
        unmountOnExit
      >
        <Collapsible.Trigger asChild>
          <HStack
            w="full"
            gap={1.5}
            alignItems="center"
            py={1}
            cursor="pointer"
            borderRadius="sm"
          >
            <Text fontSize="xs" fontWeight="normal" color="fg.muted">
              {headerLabel}
            </Text>

            <TraceExpandChevron
              isExpanded={isOpen}
              tracePhase={tracePhase}
              onHideMessage={onHideMessage}
            />
          </HStack>
        </Collapsible.Trigger>

        <Collapsible.Content>
          <Box pt={3}>
            <TraceReadFilesPanel entries={entries} tracePhase={tracePhase} />
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </VStack>
  );
}
